// ─── Subject / Paper / Chapter ───────────────────────────────────────────────

export interface Chapter {
  id: string;
  name: string;
  banglaName: string;
}

export interface Paper {
  id: string;
  name: string;
  chapters: Chapter[];
}

export interface Subject {
  id: string;
  name: string;
  code: string;
  papers: Paper[];
}

// ─── Template ────────────────────────────────────────────────────────────────

export type ExamType = 'written' | 'mcq';

export interface TemplatePart {
  label: string;
  banglaLabel: string;
  englishLabel: string;
  marks: number;
  questionType: string;
  answerType: string;
  expectedAnswerLength: string;
  difficulty: string;
  requiresCalculation: boolean;
  requiresReasoning: boolean;
  requiresDiagram: boolean;
  aiInstruction: string;
  banglaAiInstruction: string;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  subjectId: string;
  examType: ExamType;
  version: string;
  totalQuestions: number;
  questionsPerChapter?: Record<string, number>;
  stimulusInstruction?: string;
  banglaStimlusInstruction?: string;
  parts: TemplatePart[];
}

// ─── Department ────────────────────────────────────────────────────────────────

export type Department = 'science' | 'commerce' | 'arts';

export const DEPARTMENTS: {
  id: Department;
  name: string;
  banglaName: string;
  icon: string;
  color: string;
}[] = [
  { id: 'science', name: 'Science', banglaName: 'বিজ্ঞান', icon: '🔬', color: 'blue' },
  { id: 'commerce', name: 'Commerce', banglaName: 'বাণিজ্য', icon: '📊', color: 'emerald' },
  { id: 'arts', name: 'Arts & Humanities', banglaName: 'মানবিক', icon: '📚', color: 'violet' },
];

// ─── Difficulty ───────────────────────────────────────────────────────────────

export type DifficultyLevel = 'easy' | 'medium' | 'hard' | 'mixed';

export const DIFFICULTY_LEVELS: {
  id: DifficultyLevel;
  label: string;
  banglaLabel: string;
  description: string;
  color: string;
}[] = [
  {
    id: 'easy',
    label: 'Easy',
    banglaLabel: 'সহজ',
    description: 'Basic recall & definitions',
    color: 'green',
  },
  {
    id: 'medium',
    label: 'Medium',
    banglaLabel: 'মাঝারি',
    description: 'Application & explanation',
    color: 'yellow',
  },
  {
    id: 'hard',
    label: 'Hard',
    banglaLabel: 'কঠিন',
    description: 'Analysis & higher-order thinking',
    color: 'red',
  },
  {
    id: 'mixed',
    label: 'Mixed',
    banglaLabel: 'মিশ্র',
    description: 'Blend of all levels',
    color: 'indigo',
  },
];

// ─── Version ─────────────────────────────────────────────────────────────────

export type QuestionVersion = 'Bangla' | 'English';

// ─── Wizard State ─────────────────────────────────────────────────────────────

export interface WizardState {
  selectedClass: number | null; // 10 = SSC, 12 = HSC
  selectedVersion: QuestionVersion | null;
  selectedDepartment: Department | null;
  selectedSubject: Subject | null;
  selectedPaper: Paper | null;
  selectedChapters: Chapter[];
  selectedExamType: ExamType | null;
  institutionName: string;
  subjectCode: string;
  numberOfQuestions: number;
  duration: number; // minutes
  totalMarks: number;
  difficulty: DifficultyLevel;
}

export type WizardStep =
  | 'class'
  | 'version'
  | 'department'
  | 'subject'
  | 'paper'
  | 'chapter'
  | 'examType'
  | 'questions'
  | 'review'
  | 'generate';

export const WIZARD_STEPS: WizardStep[] = [
  'class',
  'department',
  'subject',
  'paper',
  'chapter',
  'examType',
  'questions',
  'version',
  'review',
  'generate',
];

export const STEP_LABELS: Record<WizardStep, string> = {
  class: 'Select Level',
  version: 'Version',
  department: 'Department',
  subject: 'Select Subject',
  paper: 'Select Paper',
  chapter: 'Select Chapter',
  examType: 'Exam Type',
  questions: 'Questions',
  review: 'Review',
  generate: 'Generate',
};

/** Returns true when a department selection is required for the given class */
export function requiresDepartment(classNum: number | null): boolean {
  return classNum !== null && classNum >= 9;
}
